import { useCallback, useMemo, useState } from 'react'

/**
 * CR-1421 item 5 (Aaron, 15 Oct 2026): a chart legend that hides and shows its
 * series on a click, as the original platform's charts did.
 *
 * Spread ``visibleSeries`` and ``onSeriesToggle`` onto ``ChartWrapper``; the
 * chart asks ``isHidden(label)`` for each series, keyed by its legend label.
 *
 * @returns the ChartWrapper props and an ``isHidden`` check
 * @example
 *   const series = useSeriesToggle()
 *   <ChartWrapper legend={...} visibleSeries={series.visibleSeries} onSeriesToggle={series.onSeriesToggle}>
 *     <Bar hide={series.isHidden('PPV')} ... />
 */
export function useSeriesToggle() {
  const [hidden, setHidden] = useState<Record<string, boolean>>({})
  const visibleSeries = useMemo(
    () => Object.fromEntries(Object.entries(hidden).map(([k, v]) => [k, !v])), [hidden])
  const onSeriesToggle = useCallback(
    (label: string, visible: boolean) => setHidden((h) => ({ ...h, [label]: !visible })), [])
  const isHidden = useCallback((label: string) => hidden[label] === true, [hidden])
  return { visibleSeries, onSeriesToggle, isHidden }
}
